// React imports
import { useCallback, useMemo } from "react"

// Next.js imports
import { useParams, usePathname } from "next/navigation"

// External SDK imports
import { useSports as _useSports, useLive } from "@azuro-org/sdk"
import {
  Game_OrderBy,
  OrderDirection,
  type SportsQuery,
} from "@azuro-org/toolkit"

// Internal hooks and utilities
import { constants } from "helpers"
import useChainProviderHook from "./useChainProvider"
import { useOvertimeFormattedSportsMarkets } from "./useSportsMarkets"
import { default as transformOvertimeData } from "@/Overtime/utils/transformOvertimeData"
import {
  useSxSportMarket,
  useSxSportsNavigation,
  useSxHomeMarkets,
} from "@/SX/hooks"
import { useSxLeagueMarkets } from "@/SX/hooks/useSxLeagueMarkets"
import { validateSlug } from "@/utils/helper"

type GraphSport = SportsQuery["sports"][0]
type GraphLeague = GraphSport["countries"][0]["leagues"][0]

export type Sport = Omit<GraphSport, "countries"> & {
  leagues: Array<
    GraphLeague & {
      countrySlug: string
      countryName: string
    }
  >
}

const useSports = () => {
  const params = useParams()
  const isTopPage = !params?.sportSlug || params?.sportSlug === "/"
  const { isLive } = useLive()

  const { isOvertimeChain, isAzuroChain, isSXChain } = useChainProviderHook()

  const props = isTopPage
    ? {
        gameOrderBy: Game_OrderBy.Turnover,
        filter: {
          limit: constants.topPageGamePerSportLimit,
        },
        isLive,
        query: {
          enabled: isAzuroChain,
        },
      }
    : {
        gameOrderBy: Game_OrderBy.StartsAt,
        orderDir: OrderDirection.Asc,
        filter: {
          sportSlug: params?.sportSlug as string,
          countrySlug: params?.countrySlug as string,
          leagueSlug: params?.leagueSlug as string,
        },
        isLive,
        query: {
          enabled: isAzuroChain,
        },
      }

  // Fetching Azuro Data
  const { data: sports, isFetching: loading } = _useSports(props as any)

  // Fetching SX Data
  const { data: sxData, isLoading: sxNavLoading } = useSxSportsNavigation()
  const { navigation: sxNavData, leagues: sxLeagues } = sxData || {}

  let formattedSports: any = []
  formattedSports = useMemo(() => {
    if (!sports?.length) {
      return
    }

    return sports.reduce<Sport[]>((newSports, sport) => {
      const { countries, ...rest } = sport

      let gamesCount = 0
      const newSport: Sport = {
        ...rest,
        leagues: [],
      }

      // For homepage, collect all games from all leagues first, then limit to 10 per sport
      if (isTopPage) {
        const allGames: Array<{ game: any; league: any; country: any }> = []

        for (const country of countries) {
          for (const league of country.leagues) {
            for (const game of league.games) {
              allGames.push({ game, league, country })
            }
          }
        }

        // Take only the first 10 games per sport
        const limitedGames = allGames.slice(
          0,
          constants.topPageGamePerSportLimit
        )

        // Group games by league for display
        const gamesByLeague = new Map()
        for (const { game, league, country } of limitedGames) {
          const leagueKey = `${country.slug}-${league.slug}`
          if (!gamesByLeague.has(leagueKey)) {
            gamesByLeague.set(leagueKey, {
              ...league,
              countrySlug: country.slug,
              countryName: country.name,
              games: [],
            })
          }
          gamesByLeague.get(leagueKey).games.push(game)
        }

        newSport.leagues = Array.from(gamesByLeague.values())
      } else {
        // For non-homepage, keep existing logic
        for (const country of countries) {
          for (const league of country.leagues) {
            newSport.leagues.push({
              ...league,
              countrySlug: country.slug,
              countryName: country.name,
            })
          }
        }
      }

      newSports.push(newSport)

      return newSports
    }, [])
  }, [sports])

  // Fetching Overtime Data
  const { openSportMarkets, isLoading, isError } =
    useOvertimeFormattedSportsMarkets()

  // Determine which SX market data to use based on current page
  const getLeagueId = useCallback(() => {
    if (sxNavLoading || !sxNavData || !sxLeagues || !params?.leagueSlug) {
      return null
    }

    const league = sxLeagues.find(l =>
      validateSlug(l.label, params?.leagueSlug as string)
    )

    return league?.leagueId || null
  }, [sxNavLoading, sxNavData, sxLeagues, params?.leagueSlug])

  const getSportId = useCallback(() => {
    if (sxNavLoading || !sxNavData || !params?.sportSlug) {
      return 0
    }
    const sport = sxNavData.sports.find(
      s => s.name.toLowerCase().replace(/\s+/g, "-") === params?.sportSlug
    )
    return sport?.sportId || 0
  }, [sxNavLoading, sxNavData, params?.sportSlug])

  const leagueId = getLeagueId()
  const sportId = getSportId()
  const pathname = usePathname()

  // Determine page type based on pathname pattern
  const isHomePage = pathname === "/"
  const pathSegments = pathname
    ? pathname.split("/").filter(segment => segment !== "")
    : []
  const isSportPage = pathSegments.length === 1 // /[sport]
  const isLeaguePage = pathSegments.length >= 2 // /[sport]/[country]/[league] or /[sport]/[league]

  // Consolidated call to useSxStreamingActiveMarkets for home page only
  const {
    sports: homeMarkets,
    isLoading: homeMarketsLoading,
    error: homeMarketsError,
  } = useSxHomeMarkets(isSXChain && !sxNavLoading && !!sxNavData && isHomePage)

  // Always call the hooks, but control enabled state

  const {
    sports: leagueMarkets,
    isLoading: leagueMarketsLoading,
    error: leagueMarketsError,
  } = useSxLeagueMarkets(
    leagueId || 0,
    isSXChain && !sxNavLoading && !!sxNavData && isLeaguePage && !!leagueId
  )

  // Always call the hook, but control enabled state
  const {
    data: sportMarkets,
    isLoading: sportMarketsLoading,
    error: sportMarketsError,
  } = useSxSportMarket(+sportId, {
    liveOnly: isLive,
    enabled:
      isSXChain && !sxNavLoading && !!sxNavData && isSportPage && !!sportId,
  })

  // Determine which data to use based on the current context
  const getSxMarketData = () => {
    let context = "default"

    if (isHomePage) {
      context = "home"
    } else if (isLeaguePage && leagueId) {
      context = "league"
    } else if (isSportPage && sportId) {
      context = "sport"
    }

    switch (context) {
      case "home": {
        return {
          data: homeMarkets,
          isLoading: homeMarketsLoading,
          isError: !!homeMarketsError,
        }
      }

      case "league": {
        return {
          data: leagueMarkets,
          isLoading: leagueMarketsLoading,
          isError: !!leagueMarketsError,
        }
      }

      case "sport": {
        return {
          data: sportMarkets?.sports,
          isLoading: sportMarketsLoading,
          isError: !!sportMarketsError,
        }
      }

      default: {
        return {
          data: homeMarkets,
          isLoading: homeMarketsLoading,
          isError: !!homeMarketsError,
        }
      }
    }
  }

  const {
    data: sxMarkets,
    isLoading: sxLoading,
    isError: sxError,
  } = getSxMarketData()

  // Determine the provider and return appropriate data
  const getProviderData = () => {
    switch (true) {
      case isOvertimeChain:
        return {
          sports: transformOvertimeData(openSportMarkets, isTopPage),
          loading: isLoading,
          isError: isError,
          isBackgroundFetching: false,
          fetchingProgress: 100,
          isComplete: true,
        }

      case isAzuroChain:
        return {
          sports: formattedSports,
          loading: loading,
          isError: false,
          isBackgroundFetching: false,
          fetchingProgress: 100,
          isComplete: true,
        }

      case isSXChain:
        return {
          sports: sxMarkets,
          loading: sxNavLoading || sxLoading,
          isError: sxError,
        }

      default:
        // Fallback to Azuro data
        return {
          sports: formattedSports,
          loading: loading,
          isError: false,
          isBackgroundFetching: false,
          fetchingProgress: 100,
          isComplete: true,
        }
    }
  }

  return getProviderData()
}

export default useSports
